/// <reference types="@types/google.maps" />
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { defaultFocusZoom, defaultInitialZoom } from 'features/store/constants';
import { debounce } from 'lodash-es';
import { Marker } from 'react-native-svg';
import { getRandomId } from 'utils';

import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { createGoogleMapKeyboardInputObserver } from 'hooks/use-map/label-google-map-keyboard-input.web';
import { useLocale } from 'state/intl';
import { primitive } from 'styles/constants';
import { isValidPosition } from 'utils/geolocation';
import noop from 'utils/noop';

import mapDefaults from './defaults';
import {
  CenterPointExtended,
  IMarker,
  IMarkerInput,
  IUseMapExtendedParam,
  IUseMapProvider,
  MarkerTypes,
  PanToFn,
} from './types';
import { checkSingleUseMarker, getKeyForMarkerInput, getMarkerZIndex } from './utils';

const MapContainer = Box.withConfig({
  backgroundColor: primitive.$mayoEggWhite,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  height: '100%',
  width: 'full',
});

const useGoogleMap: IUseMapProvider = (props: IUseMapExtendedParam, enabled = true) => {
  const { startPosition, disableControls = false, onDragEnd } = props;
  const { region } = useLocale();
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const center = useRef<CenterPointExtended>({
    ...(startPosition || defaultPosition),
    zoom: defaultInitialZoom,
  });

  const getCurrentCenter = useCallback(() => {
    return center.current;
  }, []);

  const domRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef<Record<string, IMarkerInput & { marker: google.maps.Marker | Marker }>>(
    {}
  );
  const selectedMarkerKey = useRef<string | undefined>(undefined);
  // track when the map container has been loaded into the dom
  const [isMapMounted, setIsMapMounted] = useState(false);

  const mapListeners = useRef<Record<string, google.maps.MapsEventListener[]>>({});

  const addListener = (eventName: string, map: google.maps.Map, cb: Function, id: string) => {
    if (!mapListeners.current[id]) {
      mapListeners.current[id] = [];
    }

    const dereg = map.addListener(eventName, cb);
    if (dereg) {
      mapListeners.current[id] = mapListeners.current[id].concat(dereg);
    }
  };

  const clearListenerCache = (id: string) => {
    const handlers = mapListeners.current[id] || [];
    handlers.forEach(handler => {
      if (typeof handler.remove === 'function') {
        handler?.remove();
      }
    });
    mapListeners.current[id] = [];
  };

  /*
   * SETUP MAP
   *
   * This loads the map api internally, sets up some options
   * and begins the whole process.
   *
   * NOTES:
   * - This needs to be an effect
   * - It has required listener clean ups
   * - Can not re-render map created from this hook, will error.
   *
   */

  const prepareMap = useCallback(
    (mapId: string): (() => void) | undefined => {
      if (!libraryLoaded || !window.google?.maps) {
        return;
      }
      const options = {
        center: center.current,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
        disableDefaultUI: true,
        zoom: center.current.zoom,
        gestureHandling: disableControls ? 'none' : 'greedy',
      } as google.maps.MapOptions;

      const keyboardInputObserver = createGoogleMapKeyboardInputObserver();
      const mapInstance = new window.google.maps.Map(domRef?.current!, options);
      keyboardInputObserver.observeMapContainer(domRef.current);
      setMap(mapInstance);

      const setClampedMapBounds = () => {
        const mapCenter = mapInstance.getCenter();
        if (!mapCenter) {
          return;
        }
        center.current = {
          lat: mapCenter.lat(),
          lng: mapCenter.lng(),
          zoom: mapInstance.getZoom() || center.current.zoom,
        };
      };

      window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const mapZoom = mapInstance.getZoom();
        center.current = { ...center.current, zoom: mapZoom };
        setClampedMapBounds();
      });

      /**
       *  Called by dragstart and zoom_changed listeners to prevent keyboard remaining open on android devices
       *  when dragging/zooming map while search field has focus
       */
      const blurActiveElement = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur?.();
      };

      addListener('dragstart', mapInstance, blurActiveElement, mapId);
      addListener(
        'dragend',
        mapInstance,
        () => {
          const mapCenter = mapInstance.getCenter();
          if (mapCenter) {
            center.current = {
              lat: mapCenter.lat(),
              lng: mapCenter.lng(),
              zoom: center.current.zoom,
            };
          }
          onDragEnd();
        },
        mapId
      );

      addListener(
        'zoom_changed',
        mapInstance,
        debounce(() => {
          blurActiveElement();
          const mapZoom = mapInstance.getZoom();
          const mapCenter = mapInstance.getCenter();

          if (mapCenter) {
            center.current = {
              lat: mapCenter.lat(),
              lng: mapCenter.lng(),
              zoom: mapZoom ?? center.current.zoom,
            };
          }
        }, 500),
        mapId
      );

      return () => keyboardInputObserver.disconnect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableControls, center, libraryLoaded]
  );

  useEffect(() => {
    const shouldLoad = !!domRef.current && enabled;

    if (!shouldLoad) {
      return;
    }

    const mapId = getRandomId();
    const cleanupKeyboardInputObserver = prepareMap(mapId);

    return () => {
      cleanupKeyboardInputObserver?.();
      clearListenerCache(mapId);
    };
    /*
     * NOTE:
     * - Only run when we know the map container has been loaded in the dom ~ isMapMounted
     * - isMapMounted starts as false and is only set to true when we have a dom ref
     * - And the domRef.current will be set
     */
  }, [isMapMounted, prepareMap, enabled]);

  const onLoadedIntoDom = useCallback((ref: HTMLDivElement) => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setIsMapMounted(true);
    }
  }, []);

  const focusToPoint = useCallback<PanToFn>(
    newPosition => {
      if (!libraryLoaded || !map) {
        return;
      }
      if (!map || !isValidPosition(newPosition)) {
        return;
      }
      const zoom = Math.max(defaultFocusZoom, center.current.zoom || defaultFocusZoom);

      map.panTo({ lat: newPosition.lat, lng: newPosition.lng });
      map.setZoom(zoom);
      center.current = { lat: newPosition.lat, lng: newPosition.lng, zoom };
    },
    [map]
  );

  /**
   * Adjusts map viewport to ensure all points are visible
   * @param points - Array of map points to fit in viewport
   *
   * Creates a bounding box that includes all points and adds padding (expandFactor)
   * to ensure points aren't too close to the edges of the map
   */
  const focusToPoints = useCallback(
    (points: any[]) => {
      if (!libraryLoaded || !map || points.length === 0) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(point => bounds.extend(point));

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const latSpan = ne.lat() - sw.lat();
      const lngSpan = ne.lng() - sw.lng();
      const expandFactor = 0.4;

      bounds.extend(
        new google.maps.LatLng(sw.lat() - latSpan * expandFactor, sw.lng() - lngSpan * expandFactor)
      );
      bounds.extend(
        new google.maps.LatLng(ne.lat() + latSpan * expandFactor, ne.lng() + lngSpan * expandFactor)
      );

      map.fitBounds(bounds, 50);
    },
    [libraryLoaded, map]
  );

  /*
   * Creates a marker for a given type and location. If marker
   * already placed on map, updates accordingly.
   */
  const createMarker = useCallback(
    (markerInput: IMarker): string => {
      if (!libraryLoaded || !map) {
        return '';
      }

      const { type, location, onPress, isSelected, icon } = markerInput;
      const key = getKeyForMarkerInput(markerInput, checkSingleUseMarker(type));

      if (!isValidPosition(location)) {
        return key; // bail if location is not known
      }

      // This updates the marker if it has already been added to map.
      // Set selected marker key
      selectedMarkerKey.current = isSelected ? key : selectedMarkerKey.current;

      const existingMarker = markers.current[key]?.marker as google.maps.Marker;
      if (existingMarker) {
        existingMarker.setMap(map);
        existingMarker.setTitle(markerInput.title ?? '');
        markers.current[key].title = markerInput.title;

        window.google.maps.event.clearInstanceListeners(existingMarker);
        window.google.maps.event.addListener(existingMarker, 'click', onPress!);

        // Update Icon
        if (markers.current[key].type !== type || markers.current[key].isSelected !== isSelected) {
          markers.current[key].type = type;
          markers.current[key].isSelected = isSelected;
          existingMarker.setIcon(icon as string);
        }

        // Update position
        existingMarker.setPosition(location);
        return key;
      }

      const marker = new window.google.maps.Marker({
        position: location,
        map,
        clickable: ![MarkerTypes.User, MarkerTypes.UserManual].includes(type) && !!onPress,
        icon: icon as string,
        title: markerInput.title,
        visible: !!icon,
        zIndex: getMarkerZIndex(type, isSelected),
      });
      if (onPress && ![MarkerTypes.User, MarkerTypes.UserManual].includes(type)) {
        window.google.maps.event.addListener(marker, 'click', onPress);
      }

      markers.current[key] = { id: key, marker, type, location, isSelected, onPress };
      return key;
    },
    [libraryLoaded, map]
  );

  const createMarkerList = useCallback(
    (markerList: IMarker[]) => {
      const existingMarkersMap = new Map(
        Object.entries(markers.current).map(([key, value]) => [key, value])
      );

      const newMarkersMap = new Map(
        markerList.map(marker => [
          getKeyForMarkerInput(marker, checkSingleUseMarker(marker.type)),
          marker,
        ])
      );

      existingMarkersMap.forEach((value, key) => {
        if (!newMarkersMap.has(key)) {
          (value.marker as google.maps.Marker).setMap(null);
          delete markers.current[key];
        }
      });

      markerList.forEach(markerInput => {
        const key = getKeyForMarkerInput(markerInput, checkSingleUseMarker(markerInput.type));
        const existingMarker = existingMarkersMap.get(key);

        if (existingMarker) {
          const needsUpdate =
            existingMarker.type !== markerInput.type ||
            existingMarker.isSelected !== markerInput.isSelected ||
            existingMarker.location.lat !== markerInput.location.lat ||
            existingMarker.location.lng !== markerInput.location.lng;

          if (needsUpdate) {
            createMarker(markerInput);
          }
        } else {
          createMarker(markerInput);
        }
      });
    },
    [createMarker]
  );

  const mapElement = React.createElement(MapContainer, { ref: onLoadedIntoDom });

  return {
    actualMapCenterLatLng: center.current,
    mapComponent: mapElement,
    focusToPoint,
    focusToPoints,
    renderMarkers: noop,
    createMarker,
    createMarkerList,
    getCurrentCenter,
  };
};

export default useGoogleMap;
